import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  OnInit,
  Output, OnDestroy,
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogRef,
  MatDialogModule,
  MatDialogContent,
  MatDialogActions,
} from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import {
  FormArray,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
} from '@angular/forms';
import { Amenity, PropertyAmenity } from '../../../models/property.model';
import { PropertyService } from '../../../services/property.service';
import { Subject, takeUntil } from 'rxjs';
import { SnackBarService } from '../../../../../shared/services/snackbar.service';
import { MatProgressSpinner } from "@angular/material/progress-spinner";
import { LoaderComponent } from '../../../../../shared/general/components/loader/loader.component';
import { AmenityService } from '../../../services/amenity.service';

interface DialogData {
  propertyId: string;
  currentAmenities: PropertyAmenity[];   // what the property already has
}

@Component({
  selector: 'app-amenities-edit',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatIconModule,
    MatCheckboxModule,
    ReactiveFormsModule,
    MatProgressSpinner,
    LoaderComponent
],
  templateUrl: './amenities-edit.html',
  styleUrl: './amenities-edit.scss',
})
export class AmenitiesEdit implements OnInit, OnDestroy {
  private snackbar = inject(SnackBarService);

  private readonly dialogRef = inject(MatDialogRef<AmenitiesEdit>);
  private readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  private readonly propertyService = inject(PropertyService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();
  private readonly amenityService = inject(AmenityService);

  // -----------------------------------------------------------------
  // UI state
  loading = false;
  amenitiesLoaded = false;
  allAmenities: Amenity[] = [];
  categories: string[] = [];

  // -----------------------------------------------------------------
  // Form
  amenitiesForm = new FormGroup({
    amenities: new FormArray<FormControl<boolean>>([]),
  });

  // -----------------------------------------------------------------
  // Outputs (used by the parent that opens the dialog)
  @Output() cancel = new EventEmitter<void>();
  @Output() save   = new EventEmitter<string[]>();
  // -----------------------------------------------------------------
  ngOnInit(): void {
    this.loadAllAmenities();
    this.categories = this.amenityService.getAmenityCategories();
  }

  // -----------------------------------------------------------------
  // private loadAllAmenities(): void {
  //   this.propertyService.getAmenities().subscribe((all) => {
  //     this.allAmenities = all;

  //     const array = this.amenitiesForm.controls.amenities as FormArray<FormControl<boolean>>;

  //     this.allAmenities.forEach((amenity) => {
  //       const selected = this.data.currentAmenities.some(
  //         (pa) => pa.amenityId === amenity.id
  //       );
  //       array.push(new FormControl<boolean>(selected, { nonNullable: true }));
  //     });

  //     this.amenitiesLoaded = true;
  //     this.cdr.detectChanges();
  //   });
  // }
  private loadAllAmenities(): void {
  this.propertyService.getAmenities().subscribe((all) => {
    this.allAmenities = all || []; // ← ensure never null

    const array = this.amenitiesForm.controls.amenities as FormArray<FormControl<boolean>>;

    // Clear in case of retry
    array.clear();

    this.allAmenities.forEach((amenity) => {
      const selected = this.data.currentAmenities?.some(
        (pa) => pa.amenityId === amenity.id
      );
      array.push(new FormControl<boolean>(selected, { nonNullable: true }));
    });

    this.amenitiesLoaded = true;
    this.cdr.detectChanges();
  });
}

  // -----------------------------------------------------------------
  get amenitiesGroup(): FormArray<FormControl<boolean>> {
    return this.amenitiesForm.controls.amenities as FormArray<FormControl<boolean>>;
  }

  // -----------------------------------------------------------------
  onCancel(): void {
    this.cancel.emit();
    this.dialogRef.close();
  }

  // -----------------------------------------------------------------
  onSave(): void {
    this.loading = true;
    // 1. Build a Set → automatic deduplication
    const selected = new Set<string>();
    this.allAmenities.forEach((amenity, i) => {
      if (this.amenitiesGroup.at(i).value) {
        selected.add(amenity.id);
      }
    });

    const selectedIds = Array.from(selected); // <-- guaranteed unique

    // 2. Build request
    const request = {
      propertyId: this.data.propertyId,
      amenityIds: selectedIds,
    };

    // 3. Call service
    this.propertyService.setPropertyAmenities(request).pipe(
      takeUntil(this.destroy$) // ← cancel if component destroyed
    ).subscribe({
      next: () => {
        // 4. Refresh enriched cache (amenities, calendars, images)
        this.propertyService.refresh(this.data.propertyId);

        // 5. Notify parent + close dialog
        this.save.emit(selectedIds);
        this.loading = false;
        this.dialogRef.close(selectedIds);

        // Optional: success snackbar
        this.snackbar.open('✅ Amenities saved', ' ', {
          duration: 5000,
          panelClass: ['success-snackbar'],
          horizontalPosition: 'center',
          verticalPosition: 'top',
        });
      },
      error: (err) => {
        console.error('Failed to save amenities', err);
        this.loading = false;

        // Show user-friendly error
        this.snackbar.open(
          err?.error?.message || '⚠️ Failed to save amenities',
          'Close',
          { duration: 5000, panelClass: ['error-snackbar'], horizontalPosition: 'center', verticalPosition: 'top' }
        );
      },
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

}